import { STORAGE_KEYS } from '../config.js';

interface GraphSettings {
  centerForce: number;
  linkForce: number;
  repelMultiplier: number;
  spreadMultiplier: number;
  tagForce: number;
  textFadeThreshold: number;
  nodeSizeMultiplier: number;
  linkThicknessMultiplier: number;
  [key: string]: number;
}

interface SliderConfig {
  key: string;
  label: string;
  section: 'force' | 'visual';
  min: number;
  max: number;
  step: number;
  format: (v: number) => string;
}

interface GraphRendererForSettings {
  updateForceSettings: (settings: Partial<GraphSettings>) => void;
  updateVisualSettings: (settings: Partial<GraphSettings>) => void;
}

const DEFAULT_SETTINGS: GraphSettings = {
  centerForce: 0.995,
  linkForce: 0.02,
  repelMultiplier: 1.0,
  spreadMultiplier: 1.0,
  tagForce: 1.0,
  textFadeThreshold: 1,
  nodeSizeMultiplier: 1.0,
  linkThicknessMultiplier: 2.0,
};

const FORCE_KEYS = ['centerForce', 'linkForce', 'repelMultiplier', 'spreadMultiplier', 'tagForce'];

const SLIDER_CONFIGS: SliderConfig[] = [
  { key: 'centerForce', label: 'Center Force', section: 'force', min: 0.985, max: 1.0, step: 0.001, format: v => v.toFixed(3) },
  { key: 'linkForce', label: 'Link Force', section: 'force', min: 0.005, max: 0.06, step: 0.001, format: v => v.toFixed(3) },
  { key: 'repelMultiplier', label: 'Repel Force', section: 'force', min: 0.3, max: 3.0, step: 0.1, format: v => v.toFixed(1) + 'x' },
  { key: 'spreadMultiplier', label: 'Link Distance', section: 'force', min: 0.5, max: 2.0, step: 0.1, format: v => v.toFixed(1) + 'x' },
  { key: 'tagForce', label: 'Tag Force', section: 'force', min: 0.2, max: 5.0, step: 0.1, format: v => v.toFixed(1) + 'x' },
  { key: 'textFadeThreshold', label: 'Text Fade Distance', section: 'visual', min: -3.0, max: 3.0, step: 0.1, format: v => (v >= 0 ? '+' : '') + v.toFixed(1) + 'x' },
  { key: 'nodeSizeMultiplier', label: 'Node Size', section: 'visual', min: 0.1, max: 5.0, step: 0.1, format: v => v.toFixed(1) + 'x' },
  { key: 'linkThicknessMultiplier', label: 'Link Thickness', section: 'visual', min: 0.2, max: 10.0, step: 0.1, format: v => v.toFixed(1) + 'x' },
];

function loadSettings(): GraphSettings {
  const saved = localStorage.getItem(STORAGE_KEYS.GRAPH_SETTINGS);
  if (saved) {
    try { return { ...DEFAULT_SETTINGS, ...JSON.parse(saved) }; }
    catch { return { ...DEFAULT_SETTINGS }; }
  }
  return { ...DEFAULT_SETTINGS };
}

function saveSettings(settings: GraphSettings): void {
  localStorage.setItem(STORAGE_KEYS.GRAPH_SETTINGS, JSON.stringify(settings));
}

export function getGraphSettings(): GraphSettings {
  return loadSettings();
}

export function createGraphSettingsPanel(container: HTMLElement, graphRenderer: GraphRendererForSettings): { dispose: () => void } {
  let isOpen = false;
  let current = loadSettings();
  let forceTimer: ReturnType<typeof setTimeout> | null = null;

  const toggle = document.createElement('button');
  toggle.className = 'graph-settings-toggle';
  toggle.setAttribute('aria-label', 'Graph settings');
  toggle.innerHTML = `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>`;

  const panel = document.createElement('div');
  panel.className = 'graph-settings-panel';
  panel.style.display = 'none';

  const forceSliders = SLIDER_CONFIGS.filter(c => c.section === 'force');
  const visualSliders = SLIDER_CONFIGS.filter(c => c.section === 'visual');

  function renderSlider(cfg: SliderConfig): string {
    return `
      <div class="graph-settings-item">
        <label class="graph-settings-label">
          <span>${cfg.label}</span>
          <span class="graph-settings-value" data-value="${cfg.key}">${cfg.format(current[cfg.key])}</span>
        </label>
        <input type="range" class="range-slider" data-setting="${cfg.key}"
          min="${cfg.min}" max="${cfg.max}" step="${cfg.step}" value="${current[cfg.key]}">
      </div>`;
  }

  panel.innerHTML = `
    <div class="graph-settings-header">
      <span class="graph-settings-title">GRAPH</span>
      <button class="graph-settings-reset" title="Reset to defaults">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/></svg>
      </button>
      <button class="graph-settings-close">&times;</button>
    </div>
    <div class="graph-settings-body">
      <div class="graph-settings-section">
        <h4 class="graph-settings-section-title">Forces</h4>
        ${forceSliders.map(renderSlider).join('')}
      </div>
      <div class="graph-settings-section">
        <h4 class="graph-settings-section-title">Appearance</h4>
        ${visualSliders.map(renderSlider).join('')}
      </div>
    </div>
  `;

  container.appendChild(toggle);
  container.appendChild(panel);

  toggle.addEventListener('click', () => {
    isOpen = !isOpen;
    panel.style.display = isOpen ? '' : 'none';
    toggle.style.display = isOpen ? 'none' : '';
  });

  panel.querySelector('.graph-settings-close')!.addEventListener('click', () => {
    isOpen = false;
    panel.style.display = 'none';
    toggle.style.display = '';
  });

  panel.querySelector('.graph-settings-reset')!.addEventListener('click', () => {
    current = { ...DEFAULT_SETTINGS };
    saveSettings(current);
    for (const cfg of SLIDER_CONFIGS) {
      const slider = panel.querySelector(`[data-setting="${cfg.key}"]`) as HTMLInputElement | null;
      const valueEl = panel.querySelector(`[data-value="${cfg.key}"]`);
      if (slider) slider.value = String(current[cfg.key]);
      if (valueEl) valueEl.textContent = cfg.format(current[cfg.key]);
    }
    applyForce();
    applyVisual();
  });

  panel.addEventListener('input', (e) => {
    const target = e.target as HTMLElement;
    if (!target.classList.contains('range-slider')) return;
    const key = (target as HTMLInputElement).dataset.setting!;
    const value = parseFloat((target as HTMLInputElement).value);
    current[key] = value;

    const cfg = SLIDER_CONFIGS.find(c => c.key === key);
    const valueEl = panel.querySelector(`[data-value="${key}"]`);
    if (valueEl && cfg) valueEl.textContent = cfg.format(value);

    if (FORCE_KEYS.includes(key)) {
      if (forceTimer) clearTimeout(forceTimer);
      forceTimer = setTimeout(() => {
        applyForce();
        saveSettings(current);
      }, 200);
    } else {
      applyVisual();
      saveSettings(current);
    }
  });

  function applyForce() {
    graphRenderer.updateForceSettings({
      centerForce: current.centerForce,
      linkForce: current.linkForce,
      repelMultiplier: current.repelMultiplier,
      spreadMultiplier: current.spreadMultiplier,
      tagForce: current.tagForce,
    });
  }

  function applyVisual() {
    graphRenderer.updateVisualSettings({
      textFadeThreshold: current.textFadeThreshold,
      nodeSizeMultiplier: current.nodeSizeMultiplier,
      linkThicknessMultiplier: current.linkThicknessMultiplier,
    });
  }

  return {
    dispose() {
      if (forceTimer) clearTimeout(forceTimer);
      toggle.remove();
      panel.remove();
    },
  };
}
